import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Stethoscope, User, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown, Copy, Check } from 'lucide-react';
import { ICON_PX } from '../ui/lib/iconSizes';

export function ChatMessage({ message, onFeedback }) {
  const isUser = message.role === 'user';
  const [showCitations, setShowCitations] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleFeedback = (rating) => {
    setFeedback(rating);
    onFeedback?.(message.messageId, rating);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isUser) {
    return (
      <div className="flex justify-end mb-3">
        <div className="max-w-[85%] flex items-start gap-2">
          <div className="bg-aqua text-white rounded-lg px-3 py-2 text-body-sm">
            {message.content}
          </div>
          <div className="shrink-0 w-6 h-6 rounded-full bg-aqua/20 flex items-center justify-center mt-0.5">
            <User size={ICON_PX.sm} className="text-aqua" />
          </div>
        </div>
      </div>
    );
  }

  const hasCitations = message.citations?.length > 0;

  return (
    <div className="flex justify-start mb-3">
      <div className="max-w-[85%] flex items-start gap-2">
        <div className="shrink-0 w-6 h-6 rounded-full bg-royal/20 flex items-center justify-center mt-0.5">
          <Stethoscope size={ICON_PX.sm} className="text-royal" />
        </div>
        <div className="min-w-0">
          <div className="bg-slate-50 border border-nn-border rounded-lg px-3 py-2 text-body-sm text-text-dark">
            <div className="prose prose-sm max-w-none [&>p]:mb-1.5 [&>p:last-child]:mb-0 [&>ul]:mb-1.5 [&>ul]:ml-4 [&>ul]:list-disc [&>ol]:mb-1.5 [&>ol]:ml-4 [&>ol]:list-decimal [&>h3]:text-body [&>h3]:font-semibold [&>h3]:text-aqua [&>h3]:mt-3 [&>h3]:mb-1 [&>h3:first-child]:mt-0 [&_li]:mb-0.5">
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </div>
          </div>

          {/* Actions row */}
          <div className="flex items-center gap-1.5 mt-1 px-1">
            <button
              onClick={handleCopy}
              className="p-0.5 text-text-muted hover:text-text-dark transition-colors"
              title="Copy response"
            >
              {copied
                ? <Check size={ICON_PX.sm} className="text-green-nn" />
                : <Copy size={ICON_PX.sm} />}
            </button>

            {message.messageId && (
              <>
                <button
                  onClick={() => handleFeedback('up')}
                  className={`p-0.5 transition-colors ${feedback === 'up' ? 'text-green-nn' : 'text-text-muted hover:text-text-dark'}`}
                  title="Helpful"
                >
                  <ThumbsUp size={ICON_PX.sm} />
                </button>
                <button
                  onClick={() => handleFeedback('down')}
                  className={`p-0.5 transition-colors ${feedback === 'down' ? 'text-red-critical' : 'text-text-muted hover:text-text-dark'}`}
                  title="Not helpful"
                >
                  <ThumbsDown size={ICON_PX.sm} />
                </button>
              </>
            )}
          </div>

          {/* Citations */}
          {hasCitations && (
            <div className="mt-1 px-1">
              <button
                onClick={() => setShowCitations(!showCitations)}
                className="flex items-center gap-1 text-caption text-text-muted hover:text-text-dark transition-colors"
              >
                {showCitations ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                {message.citations.length} source{message.citations.length !== 1 ? 's' : ''}
              </button>
              {showCitations && (
                <div className="mt-1 space-y-1">
                  {message.citations.map((c, i) => (
                    <div
                      key={i}
                      className="bg-surface-warm rounded px-2 py-1 text-caption text-text-muted"
                    >
                      <span className="font-medium text-text-dark">{c.title}</span>
                      {c.snippet && (
                        <p className="mt-0.5 line-clamp-2">{c.snippet}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
